// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Button as WeaveButton,
  ButtonSizes as WeaveButtonSizes,
  ButtonVariants as WeaveButtonVariants,
  Slider as WeaveSlider,
} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {usePanelContext} from '../../Panel/PanelContextProvider';
// eslint-disable-next-line wandb/no-deprecated-imports
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';
import {type useTrackSettingsChange} from '../../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {usePanelConfigContext} from '../PanelConfigContext';
import {type RunsLinePlotConfig} from '../types';

export const EditMaxRuns = ({
  config,
  isBucketing,
  isGrouped,
  settingsToTrack,
  setRunOnApply,
  trackPanelSettingsChange,
  updateConfig,
}: {
  config: RunsLinePlotConfig;
  isBucketing: boolean;
  isGrouped: boolean;
  settingsToTrack: Record<string, any>;
  setRunOnApply: (runOnApply: () => void) => void;
  trackPanelSettingsChange: ReturnType<typeof useTrackSettingsChange>;
  updateConfig: (config: RunsLinePlotConfig) => void;
}) => {
  const {inheritedSettings} = usePanelContext();
  const {limit, maxRunsLimit} = usePanelConfigContext();

  return (
    <Tailwind>
      <div
        className="flex items-center justify-between"
        data-test="edit-data-max-runs">
        <SettingLabel
          htmlFor="max-runs"
          text={`Max ${isGrouped ? 'group' : 'run'}s to show`}
          className="w-full"
        />
        <WeaveButton
          onClick={() => {
            settingsToTrack.current = {
              ...settingsToTrack.current,
              numberOfRuns: undefined,
            };
            setRunOnApply(() => () => {
              trackPanelSettingsChange({
                setting: 'numberOfRuns',
                settingValue: settingsToTrack.current?.numberOfRuns?.toString(),
              });
            });
            updateConfig({
              limit: undefined,
            });
          }}
          size={WeaveButtonSizes.Medium}
          icon="randomize-reset-reload"
          aria-label="Reset"
          tooltip="Reset"
          variant={WeaveButtonVariants.Ghost}
        />
      </div>
      <div className={'mt-4 grid  grid-cols-[1fr_auto] items-center gap-12'}>
        <WeaveSlider.Root
          name="max-runs"
          onValueChange={newVal => {
            settingsToTrack.current = {
              ...settingsToTrack.current,
              limit: newVal[0],
            };
            setRunOnApply(() => () => {
              trackPanelSettingsChange({
                setting: 'limit',
                settingValue: settingsToTrack.current?.limit?.toString(),
              });
            });
            updateConfig({
              limit: newVal[0],
            });
          }}
          step={1}
          min={1}
          max={maxRunsLimit}
          value={[limit]}>
          <WeaveSlider.Track>
            <WeaveSlider.Range />
          </WeaveSlider.Track>
          <WeaveSlider.Thumb />
        </WeaveSlider.Root>
        <div data-test="max-runs-display">
          <WeaveSlider.Display
            // TODO: think about updating the API here
            isDirty={
              config.limit != null &&
              config.limit !== inheritedSettings?.linePlot?.limit
            }
            onChange={newVal => {
              if (newVal < 1) {
                newVal = 1;
              }
              if (newVal > maxRunsLimit) {
                newVal = maxRunsLimit;
              }

              settingsToTrack.current = {
                ...settingsToTrack.current,
                numberOfRuns: newVal,
              };
              setRunOnApply(() => () => {
                trackPanelSettingsChange({
                  setting: 'numberOfRuns',
                  settingValue:
                    settingsToTrack.current?.numberOfRuns?.toString(),
                });
              });
              updateConfig({
                limit: newVal,
              });
            }}
            step={1}
            min={1}
            max={maxRunsLimit}
            value={limit}
          />
        </div>
      </div>
    </Tailwind>
  );
};
